import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import * as Models from '../../models';
import { ISOTService } from '../../services/ISOTService';
import { MatStepper } from '@angular/material/stepper';
import { RecommendationDialogProduct, RecommendationDialogProductGroup } from './productrecommendationdialog.model';
import { SOTProductTagViewModel } from '../../models';
import Utils from '../../../core/shared/utils';
import { BaseUnit } from '../../../core/shared/models';

export interface ProductRecommendationDialogConfig {
  recommendations: Models.SOTItemGroupRecommendationViewModel[];
  isItemTagEnabled: boolean;
  hasTagSelected: boolean;
  sot_service: ISOTService;
}

@Component({
  selector: 'sot-productRecommendationDialog',
  templateUrl: './productrecommendationdialog.component.html',
  styleUrls: ['./productrecommendationdialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductRecommendationDialogComponent implements OnInit {
  @ViewChild("stepper") stepper: MatStepper;

  public recommendations: Models.SOTItemGroupRecommendationViewModel[];
  public buttonBack: string;
  public showProductDetails: boolean = false;
  public isItemTagEnabled: boolean = false;
  public hasTagSelected: boolean = false;
  public selectedTagFilter: number[];
  public showProductAdditionalInfo: boolean = false;
  public showExtraAdditionalInfo: boolean = false;
  public selectedProduct: Models.SOTProductViewModel;
  public extraPrice: number;

  public productGroups: RecommendationDialogProductGroup[];
  public selectedProductGroupIndex: number;
  public get selectedProductGroup(): RecommendationDialogProductGroup {
    return this.productGroups[this.selectedProductGroupIndex];
  }

  public product: Models.SOTProductViewModel;
  public itemTagsViewModel: Models.SOTProductTagViewModel[];
  public extraGroups: Models.SOTProductExtraGroupViewModel[];
  public tempBasket: Models.SOTBasketViewModel;
  private _sotService: ISOTService;
  public get tempProduct(): Models.SOTBasketProductViewModel {
    return this.tempBasket.products[0];
  }
  private _baseUnit = BaseUnit;
  /**baseUnit */
  public get baseUnit() {
    const me = this;
    return me._baseUnit;
  }

  constructor(
    private _changeDetectorRef: ChangeDetectorRef,
    public dialogRef: MatDialogRef<ProductRecommendationDialogComponent, any>,
    @Inject(MAT_DIALOG_DATA) public data: ProductRecommendationDialogConfig,
  ) {
    const me = this;
    me._sotService = me.data.sot_service;
    me.isItemTagEnabled = me.data.isItemTagEnabled;
    me.hasTagSelected = me.data.hasTagSelected;
    me.recommendations = me.data.recommendations;
    me.productGroups = me.recommendations.map((recommendation) => me.mapRecommendationToProductGroupViewModel(recommendation)).filter(productGroup => productGroup.products.length > 0);
    me.selectedProductGroupIndex = 0;
  }

  ngOnInit(): void {
    const me = this;
    me.setBackButtonText();
    // dialog beenden wenn keine Auswahl möglich ist
    if (me.productGroups.length === 0) {
      setTimeout(() => {
        // ! TODO timeout momemntan, sonst ein fehler auslöst "Expression has changed after it was checked. Previous value: '@slideDialog: enter'. Current value: '@slideDialog: exit'."
        // TODO den dialog erst garnicht öffnen, wenn es keien momentan verfügbaren Empfehlungen hat?
        me.dialogRef.close();
      }, 0);
    }
    me.PushChanges();
  }
  public get isModeOne(): boolean {
    return this.data.sot_service.Accessabilty.useModeOne;
  }
  public get isModeTwo(): boolean {
    return this.data.sot_service.Accessabilty.useModeTwo;
  }

  public setBackButtonText() {
    const me = this;
    if (this.selectedProductGroupIndex == 0) {
      me.buttonBack = this._sotService.TranslateKeyToLanguage(this._sotService.GetCurrentLanguage(), "button_cancel");
    } else {
      me.buttonBack = this._sotService.TranslateKeyToLanguage(this._sotService.GetCurrentLanguage(), "button_back");
    }
  }

  private PushChanges(): void {
    try {
      this._changeDetectorRef.detectChanges();
    } catch (e) {
      this.data.sot_service.coreLogService.error("ProductDialogComponent.PushChanges", "_changeDetectorRef.detectChanges (catch)", e);
    }
  }

  /** Warengruppe */
  private mapRecommendationToProductGroupViewModel(recommendation: Models.SOTItemGroupRecommendationViewModel): RecommendationDialogProductGroup {
    // Zeitplan aktualisieren
    recommendation.products.forEach(product => {
      if (product) {
        product.refreshSchedule(this._sotService.scheduleReqForRecomendationProduct());
      }
    });
    // Nur verfügbare und sichtbare Produkte abbilden
    const visibleAvailableProducts = recommendation.products
      .filter(product => product.is_available && !product.schedule.hide)
      .map(product => this.mapRecommendationToProductViewModel(product));
    return {
      title: recommendation.title,
      products: visibleAvailableProducts
    };
  }

  private mapRecommendationToProductViewModel(product: Models.SOTProductViewModel): RecommendationDialogProduct {
    return {
      label: product.label,
      available: product.is_available,
      schedule_active: product.schedule.active,
      schedule_hint: product.schedule.hint,
      price: product.price,
      has_image: product.has_image,
      image: product.image_path,
      onClick: () => this.selectProductDetail(product),
    }
  }

  private selectProductDetail(product: Models.SOTProductViewModel) {
    const me = this;
    if (product.is_available && product.schedule.active) {
      me.showProductDetails = true;
      me.product = product;
      me.itemTagsViewModel = me.data.sot_service.GetProductTagsByIds(product.product_tag_ids)
      me.extraGroups = me.product.extraGroups;
      me.tempBasket = me.data.sot_service.GetNewProductBasket(me.product);
      // Check and log missing products for extras 
      me.product.extraGroups.forEach(extragroup => {
        extragroup.extra_items.forEach(extra => {
          if (!extra.product) {
            console.error(`Missing Product for Extra [ProductID=${extra.product_id}] "${extra.title}"`);
          }
        });
      });

      // Prefill Single Option Extragroups
      me.product.extraGroups.forEach(extragroup => {
        if (extragroup.hasNoChoice && extragroup.extra_items.length > 0) {
          const extra = extragroup.extra_items[0];
          const subextra = (extra.without_subitem && !extra.with_one_subitem) ? null : extra.sub_items[0];
          me.setExtraCountTo(extragroup.max_choices, extragroup, extra, subextra);
        }
      });
      me.PushChanges();
    }
  }

  backBtn() {
    const me = this;
    const isFirstProductGroup = me.selectedProductGroupIndex == 0;
    if (isFirstProductGroup) {
      me.dialogRef.close();
    } else {
      me.stepper.previous();
      me.selectedProductGroupIndex -= 1;
      me.setBackButtonText();
    }
  }

  continueBtn() {
    const me = this;
    const isLastProductGroup = (me.productGroups.length - 1) == me.selectedProductGroupIndex
    if (isLastProductGroup) {
      me.dialogRef.close();
    } else {
      me.selectedProductGroupIndex += 1;
      me.setBackButtonText();
      if (me.stepper) {
        me.stepper.next();
      }
    }
  }

  public get visibleExtragroups(): Models.SOTProductExtraGroupViewModel[] {
    return this.extraGroups.filter(extragroup => !extragroup.hasNoChoice);
  }

  public get deposit_langkey(): string {
    return this._sotService.deposit_view ? "details_incl_deposit" : "details_plus_deposit";
  }
  // Are their visible Extras
  public get hasExtras() {
    return this.visibleExtragroups.length > 0;
  }

  public get extra_as_image() {
    const me = this;
    return me._sotService.GetSettings().extra_as_image;
  }

  public get use_eCommerce(): boolean {
    const me = this;
    return me._sotService.GetSettings().use_eCommerce;
  }

  public extraBaseUnit(extraItem: Models.SOTProductViewModel): BaseUnit {
    return extraItem.details.item_Base_Unit;
  }

  public extraPricePerUnit(extraItem: Models.SOTProductViewModel): number {
    const pricePerUnit = Utils.pricePerUnit(extraItem.priceWithExtras, extraItem.details.item_Base_Factor);
    return pricePerUnit;
  }

  /** Determines if the base price should be displayed */
  public showBasePrice(extraItem: Models.SOTProductViewModel): boolean {
    const baseUnit = extraItem.details.item_Base_Unit;
    // No base price if the unit is set to None
    if (baseUnit === BaseUnit.NONE) {
      return false;
    }
    const itemBaseFactor = extraItem.details.item_Base_Factor;
    // No base price if the unit is 1, e.g., 1 liter (no recalculation required by the customer)
    if (itemBaseFactor === 1) {
      return false;
    }
    const basePrice = extraItem.price;
    // No base price if the price is 0.00 €
    if (basePrice <= 0) {
      return false;
    }
    // Show base price if the itemBaseFactor is e.g. 250 gr., 0.75 l
    return true;
  }

  public GetExtraProductTagsByIds(tagIds: number[]) {
    const me = this;
    let extraTags: SOTProductTagViewModel[];
    if (me.hasTagSelected) {
      extraTags = me._sotService.GetProductTagsByIds(tagIds).sort((a, b) =>
        me.displaySelectedTagFirst(a.id, b.id)).splice(0, 5);
    } else {
      extraTags = me._sotService.GetProductTagsByIds(tagIds).splice(0, 5);
    }
    return extraTags;
  }

  public displaySelectedTagFirst(a_tagId: number, b_tagId: number) {
    const me = this;
    const selectedTags = (product_tag_id: number): boolean => {
      return me._sotService.GetProductTagFilter().includes((product_tag_id))
    }
    if (!selectedTags(a_tagId) && selectedTags(b_tagId)) {
      return 1;
    } else if (selectedTags(a_tagId) && !selectedTags(b_tagId)) {
      return -1;
    }
    return 0;
  }

  public fadeOutExtraBySelectedTag(extra: Models.SOTProductExtraItemViewModel): boolean {
    const me = this;
    if (me.hasTagSelected) {
      me.selectedTagFilter = me._sotService.GetProductTagFilter();
      const hasSomeProductTags = (product_tag_ids: number[]): boolean => {
        return me.selectedTagFilter.every((id => product_tag_ids.indexOf(id) >= 0))
      }
      let fadeOutExtra: boolean = false;
      if (extra.product.product_tag_ids.length > 0) {
        if (!me.isResetTagFilterActive(extra.id)) {
          fadeOutExtra = !hasSomeProductTags(extra.product.product_tag_ids);
        }
      }
      return fadeOutExtra;
    }
  }

  public openResetExtraTagFilter(extra: Models.SOTProductExtraItemViewModel) {
    const me = this;
    if (me.isExtraAvailable(extra)) {
      me._sotService.openResetExtraTagFilter().then((result) => {
        if (result) {
          me._sotService.setResetTagFilterExtraIds(extra.id);
          me.PushChanges();
        }
      });
    }
  }

  public isResetTagFilterActive(extraId: number): boolean {
    const me = this;
    return me._sotService.isResetTagFilterActive(extraId);
  }

  /** Überprüft ob das Produkt hinzugefügt werden kann. (Pflichtauswahl getroffen && ist verfügbar) */
  public get canAddProductToBasket(): boolean {
    return this.product.is_available && this.product.extraGroups.every((extragroup) => this.areExtraGroupRequirementsMet(extragroup)); // todo doko: this.tempBasket.is_complete oder so impementiern
  }

  // returns baseprice plus the price of all prefilled NoChoice Extras since we hide their price
  public getBasketProductShownBasePrice(basketProduct: Models.SOTBasketProductViewModel) {
    const sumOfPrefilledExtras = basketProduct.extra_groups.filter(eg => eg.model.hasNoChoice).reduce((a, b) => a + b.priceSum, 0);
    return basketProduct.basePrice + sumOfPrefilledExtras;
  }

  /** For toggling Extras in an ExtraGroup with max Choice = 1 */
  private toggleExtraInSingleChoice(extragroup: Models.SOTProductExtraGroupViewModel, extra: Models.SOTProductExtraItemViewModel, sub?: Models.SOTProductExtraItemViewModel) {
    if (this.isExtraAvailable(extra, sub)) {
      if (this.getCount(extragroup, extra, sub) === 0) {
        this._removeAllExtras(extragroup);
        this.addExtra(extragroup, extra, sub);
      } else {
        // In a group with max_choices 1 this will be the only Extra if it exists
        this.removeExtra(extragroup, extra, sub);
      }
    }
    this.PushChanges();
  }

  public removeExtra(extragroup: Models.SOTProductExtraGroupViewModel, extra: Models.SOTProductExtraItemViewModel, sub?: Models.SOTProductExtraItemViewModel) {
    if (this.canRemoveExtra(extragroup, extra, sub)) {
      this.tempProduct.removeExtraGroup(extragroup.query, extra.id, sub ? sub.id : 0);
    }
  }

  public addExtra(extragroup: Models.SOTProductExtraGroupViewModel, extra: Models.SOTProductExtraItemViewModel, sub?: Models.SOTProductExtraItemViewModel) {
    if (this.canAddExtra(extragroup, extra, sub)) {
      this.tempProduct.addExtraGroup(extragroup.query, extra.id, sub ? sub.id : 0);
    }
  }

  private _removeAllExtras(extragroup: Models.SOTProductExtraGroupViewModel) {
    const basketExtraGroup = this._findBasketExtraGroup(extragroup);
    if (basketExtraGroup) {
      basketExtraGroup.extras.forEach((basketExtra) => {
        basketExtra.sub_extras.forEach((basketSubExtra) => {
          this.tempProduct.removeExtraGroup(extragroup.query, basketExtra.extra_id, basketSubExtra.extra_id);
        });
        this.tempProduct.removeExtraGroup(extragroup.query, basketExtra.extra_id);
      });
    }
  }

  public getCount(extragroup: Models.SOTProductExtraGroupViewModel, extra?: Models.SOTProductExtraItemViewModel, sub?: Models.SOTProductExtraItemViewModel) {
    const existingExtraGroup = this._findBasketExtraGroup(extragroup);
    if (extra) {
      const existingExtra = existingExtraGroup ? existingExtraGroup.extras.find(e => e.extra_id === extra.id) : null;
      if (sub) {
        const existingSub = existingExtra ? existingExtra.sub_extras.find(e => e.extra_id === sub.id) : null;
        return existingSub ? existingSub.count : 0;
      }
      return existingExtra ? existingExtra.count : 0;
    }
    return existingExtraGroup ? existingExtraGroup.extras.reduce((a, b) => a + b.count, 0) : 0;
  }

  public setExtraCountTo(count: number, extragroup: Models.SOTProductExtraGroupViewModel, extra: Models.SOTProductExtraItemViewModel, sub?: Models.SOTProductExtraItemViewModel) {
    while (this.getCount(extragroup, extra, sub) > count) {
      this.removeExtra(extragroup, extra, sub);
      this.PushChanges();
    }
    while (this.getCount(extragroup, extra, sub) < count && this.canAddExtra(extragroup, extra, sub)) {
      this.addExtra(extragroup, extra, sub);
      this.PushChanges();
    }
  }

  public canAddExtra(extragroup: Models.SOTProductExtraGroupViewModel, extra: Models.SOTProductExtraItemViewModel, sub?: Models.SOTProductExtraItemViewModel) {
    const lessThanMaximumAmountOfExtra = this.getCount(extragroup) < extragroup.max_choices;
    const isAvailable = this.isExtraAvailable(extra, sub);

    return lessThanMaximumAmountOfExtra && isAvailable;
  }

  public canRemoveExtra(extragroup: Models.SOTProductExtraGroupViewModel, extra: Models.SOTProductExtraItemViewModel, sub?: Models.SOTProductExtraItemViewModel) {
    return this.getCount(extragroup, extra, sub) > 0;
  }

  public isExtraSelected(extragroup: Models.SOTProductExtraGroupViewModel, extra: Models.SOTProductExtraItemViewModel, sub?: Models.SOTProductExtraItemViewModel) {
    return this.getCount(extragroup, extra, sub) > 0;
  }

  private _findBasketExtraGroup(extragroup: Models.SOTProductExtraGroupViewModel): Models.SOTBasketItemExtraGroupViewModel {
    return this.tempProduct.extra_groups.find(eg => eg.query === extragroup.query);
  }

  public onDishClick(event: Event, extragroup: Models.SOTProductExtraGroupViewModel, extra: Models.SOTProductExtraItemViewModel, sub?: Models.SOTProductExtraItemViewModel) {
    event.stopPropagation();
    if (extragroup.max_choices === 1) {
      this.toggleExtraInSingleChoice(extragroup, extra, sub);
    } else {
      this.addExtra(extragroup, extra, sub);
    }
  }

  /** Überprüft ob die Anforderungen einer ExtraGruppe erfüllt sind
   * TODO: Sollte in SOTBasketExtraGroupViewModel extrahiert werden
   */
  public areExtraGroupRequirementsMet(extragroup: Models.SOTProductExtraGroupViewModel): boolean {
    return !extragroup.force_mode || extragroup.max_choices === this.getCount(extragroup); // todo doko: SOTProductExtraGroupViewModel.is_complete oder so impementiern
  }

  /** Berechnet den Preis von SubExtras
   * TODO: Momentan an der Oberfläche gehandhabt, Logik sollte in das BasketModel verschoben werden
   */
  public getBasketSubExtraPrice(product: Models.SOTBasketProductViewModel, extra: Models.SOTBasketItemExtraItemViewModel, sub?: Models.SOTBasketItemSubExtraItemViewModel): number {
    // TODO doko: Keine Möglichkeit im moment die Summe zu bekommen, da ein extra mit subextras nur die Summe der subextras returned und sich selbst nicht dazuzählt
    const extraPriceSum = product.model.getExtraPrices(extra.extra_id).discountPrice * sub.count;
    return sub.priceSum + extraPriceSum;
  }

  public isExtraAvailable(extra: Models.SOTProductExtraItemViewModel, sub?: Models.SOTProductExtraItemViewModel): boolean {
    return extra.product && extra.product.is_available && (sub ? sub.product && sub.product.is_available : true);
  }
  public isSubExtraAvailable(sub: Models.SOTProductExtraItemViewModel): boolean {
    return sub.product && sub.product.is_available;
  }

  public backToDishListBtn() {
    const me = this;
    me.showProductDetails = false;
    me.PushChanges();
  }

  public checkRequirementAndAddToBasket() {
    const me = this;
    const hasRequiredAge = this.product && this.product.model && this.product.model.ask_age && this.product.model.ask_age > 0;
    if (hasRequiredAge) {
      this.data.sot_service.validateAgeRequirement(this.product).then((result) => {
        result ? me._addProductToBasket() : me.backToDishListBtn();
      });
    } else {
      me._addProductToBasket();
    }
  }

  private _addProductToBasket() {
    const me = this;
    if (this.canAddProductToBasket) {
      me.showProductDetails = false;
      me.continueBtn();
      me.PushChanges();
      this.data.sot_service.GetBasket().merge(this.tempBasket);
    }
  }

  //#region Additional information 

  /** Loads default layout if no additional info is shown*/
  public loadDefaultlayout() {
    const me = this;
    return !me.showProductAdditionalInfo && !me.showExtraAdditionalInfo;
  }

  /**Toggles the visibility of Product additional information section. */
  public toggleProductAdditionalInfo(show: boolean): void {
    const me = this;
    me.showProductAdditionalInfo = show;
    me.PushChanges();
  }

  /**Toggles the visibility of Extra additional information section*/
  public toggleExtraAdditionalInfo(show: boolean) {
    const me = this;
    me.showExtraAdditionalInfo = show;
    me.PushChanges();
  }

  /** 
   * Displays the additional information for a selected product.
   * @param product - The 'Extra- product' for which the additional information is to be displayed.
   * @param event - The event object to stop propagation.
   */
  public DisplayAdditionalInfo(product: Models.SOTProductViewModel, extraPrice?: number, event?: Event,): void {
    const me = this;
    if (!me.showProductAdditionalInfo && me.showExtraAdditionalInfo) {
      me.extraPrice = extraPrice;
      event.stopPropagation();
    }
    me.selectedProduct = product;
    me.PushChanges();
  }

  /** display the "i" button if the required settings are active and additional information is included
    * @param extraProduct 
    * @returns hasAdditionalInfo
    */
  public candisplayAdditionalInformation(extraProduct: Models.SOTProductViewModel): boolean {
    const me = this;
    const settings = me._sotService.GetSettings();
    return settings && extraProduct && extraProduct.details &&
      (me.hasCO2Information(settings, extraProduct) ||
        me.hasAdditivesInformation(settings, extraProduct) ||
        me.hasNutritionInformation(settings, extraProduct) ||
        me.hasAllergeneInformation(settings, extraProduct) ||
        me.hasECommerceInformation(settings, extraProduct));
  }

  /**
  * These methods check if specific information is available for a product,
  * based on the settings and the product details.
  * 
  * @param settings - The settings that determine which information should be used.
  * @param extraProduct - The product whose information is being checked.
  * @returns A boolean value indicating whether the information is available.
  */
  private hasCO2Information(settings: Models.SOTSettingsViewModel, extraProduct: Models.SOTProductViewModel) {
    return settings.use_co2 && extraProduct.details.co2 > 0;
  }

  private hasAdditivesInformation(settings: Models.SOTSettingsViewModel, extraProduct: Models.SOTProductViewModel) {
    return settings.use_additives && Utils.ValidateArray(extraProduct.details.additives);
  }

  private hasNutritionInformation(settings: Models.SOTSettingsViewModel, extraProduct: Models.SOTProductViewModel) {
    return settings.use_nutrition_values && extraProduct.details.nutritions && Utils.ValidateArray(extraProduct.details.nutritions.nutritionItemViewModels);
  }

  private hasAllergeneInformation(settings: Models.SOTSettingsViewModel, extraProduct: Models.SOTProductViewModel) {
    return settings.use_allergens && Utils.ValidateArray(extraProduct.details.allergens);
  }

  private hasECommerceInformation(settings: Models.SOTSettingsViewModel, extraProduct: Models.SOTProductViewModel) {
    return settings.use_eCommerce && (Utils.IsNullOrWhiteSpace(extraProduct.details.item_Content) || extraProduct.details.hasProvider_Info);
  }
}
